import { useTranslation } from "react-i18next";
import {
    Bell,
    BellOff,
    MessageSquare,
    Minimize,
    Maximize,
    PencilLine,
    PhoneOff,
} from "lucide-react";
import { DisconnectButton } from "@livekit/components-react";

import { Track } from "livekit-client";

import { useBackgroundEffect } from "../../application/useBackgroundEffect";
import type { FullscreenChannel } from "../../application/useFullscreen";
import type { GongChannel } from "../../application/useGongChannel";
import type { MeetingChatChannel } from "../../application/useMeetingChat";
import type { MeetingLayoutChannel } from "../../application/useMeetingLayout";
import type { WhiteboardChannel } from "../../application/useWhiteboardChannel";
import { canShareScreen } from "../../application/screenShareSupport";
import MeetingAudioShare from "./MeetingAudioShare";
import MeetingBackground from "./MeetingBackground";
import MeetingToggle from "./MeetingControl";
import MeetingControlMenu from "./MeetingControlMenu";
import MeetingDeviceList from "./MeetingDeviceList";
import MeetingGongSettings from "./MeetingGongSettings";
import MeetingLayoutPicker from "./MeetingLayoutPicker";

type MeetingBarProps = {
    board: WhiteboardChannel;
    chat: MeetingChatChannel;
    fullscreen: FullscreenChannel;
    gong: GongChannel;
    layout: MeetingLayoutChannel;
    /** Off where the page already owns the ending. */
    canLeave: boolean;
    /** Drawn for a phone: the icons alone, on one row. */
    compact: boolean;
};

/**
 * The controls, under the video.
 *
 * Two things were wrong with the row this replaces. It had eight controls of
 * equal weight side by side, so the microphone — the one a therapist reaches
 * for mid sentence — was no easier to find than the layout picker, which is
 * touched once a month. And each was a wide pill with the label beside the
 * icon, which reads as a form rather than as a toolbar.
 *
 * Now there are six, stacked icon over label the way every call product draws
 * them, and the things that were competing with them have moved under the
 * control they belong to: which microphone, under the microphone; which
 * camera and whether the background is blurred, under the camera; sharing a
 * tab's sound without its picture, under sharing. Nothing went behind a
 * "more" button — a menu named after the thing it modifies is findable, and
 * "more" is where controls go to be lost.
 *
 * Leaving is pinned to the right, away from the rest. Mute and camera are
 * pressed dozens of times in a session; leaving is pressed once, at the end,
 * and a misfire costs the call.
 *
 * On a phone the same controls are drawn as icons on a single row. Six 80px
 * controls wrap into three rows on a 390px screen, which spends about a third
 * of the picture on a toolbar in a call whose content is a face. Nothing is
 * dropped and nothing moves behind a "more": the labels go to `sr-only`,
 * where they still carry the state for a screen reader, and the one control
 * that does go is the one the phone cannot honour — sharing a screen.
 */
export default function MeetingBar({
    board,
    chat,
    fullscreen,
    gong,
    layout,
    canLeave,
    compact,
}: MeetingBarProps) {
    const { t } = useTranslation();
    /**
     * Held here rather than inside the camera menu that shows it. A menu
     * unmounts when it closes, taking the effect that removes the blur with
     * it — so turning the blur off did nothing, and it stayed on the published
     * track for the rest of the call. The bar is on screen throughout.
     */
    const background = useBackgroundEffect();

    /** A sequence with strikes still to come, rather than a gong sounding. */
    const running = gong.nextAt !== null;

    /** The shape every control in the bar takes on this screen. */
    const shape = compact ? "room-control-icon" : "room-control-stack";
    /** Where a label goes when the icon is carrying it. */
    const labelClass = compact ? "sr-only" : undefined;

    return (
        <div
            className={`px-1 pb-[max(0.25rem,env(safe-area-inset-bottom))] ${
                compact
                    ? // 4px, not 6: measured at 390px, the six pixels are
                      // the difference between one row and two.
                      "flex flex-wrap items-center justify-center gap-1"
                    : "grid grid-cols-[1fr_auto_1fr] items-end gap-2"
            }`}
            data-testid="meeting-bar"
        >
            {/* Left gutter, so the cluster is centred on the video rather than
                on whatever room is left beside the leave button. Not on a
                phone, where the row is the whole bar and an empty column
                would push the controls off centre the other way. */}
            {!compact && <span aria-hidden="true" />}

            <div
                className={
                    compact
                        ? "contents"
                        : "flex flex-wrap items-end justify-center gap-1.5"
                }
            >
                <MeetingToggle
                    source={Track.Source.Microphone}
                    compact={compact}
                    menu={(close) => (
                        <MeetingDeviceList kind="audioinput" onSelected={close} />
                    )}
                />

                <MeetingToggle
                    source={Track.Source.Camera}
                    compact={compact}
                    menu={(close) => (
                        <>
                            <MeetingDeviceList kind="videoinput" onSelected={close} />
                            <hr className="my-1 border-room-line-soft" />
                            {/* No `close`: this is a toggle, and seeing the
                                state you just changed is the point. */}
                            <MeetingBackground channel={background} />
                        </>
                    )}
                />

                {/* A phone has no `getDisplayMedia`, so this button opened
                    nothing there — and since sharing is how a worksheet goes
                    up, that read as the session being broken rather than as
                    the phone not doing this. */}
                {canShareScreen() && (
                    <MeetingToggle
                        source={Track.Source.ScreenShare}
                        compact={compact}
                        menu={() => <MeetingAudioShare />}
                    />
                )}

                <button
                    type="button"
                    onClick={() => (board.open ? board.closeBoard() : board.openBoard())}
                    aria-pressed={board.open}
                    className={`room-control ${shape} ${
                        board.open ? "room-control-on" : ""
                    }`}
                >
                    <PencilLine size={18} aria-hidden="true" />
                    <span className={labelClass}>
                        {board.open
                            ? t("meetingControls.hideWhiteboard")
                            : t("meetingControls.whiteboard")}
                    </span>
                </button>

                <button
                    type="button"
                    onClick={() => chat.setOpen(!chat.open)}
                    aria-pressed={chat.open}
                    data-testid="chat-toggle"
                    className={`room-control ${shape} relative ${
                        chat.open ? "room-control-on" : ""
                    }`}
                >
                    <MessageSquare size={18} aria-hidden="true" />
                    <span className={labelClass}>
                        {chat.open
                            ? t("meetingControls.hideChat")
                            : t("meetingControls.chat")}
                    </span>
                    {chat.unread > 0 && (
                        // The count is in the label as well as the badge: a
                        // coloured dot alone says nothing to a screen reader.
                        <span
                            className="absolute right-1 top-1 inline-flex min-w-5 items-center justify-center rounded-full bg-room-live px-1.5 text-xs font-semibold text-white"
                            data-testid="chat-unread"
                        >
                            <span aria-hidden="true">{chat.unread}</span>
                            <span className="sr-only">
                                {t("meetingChat.unread", { count: chat.unread })}
                            </span>
                        </span>
                    )}
                </button>

                {/*
                  * The button is the therapist's, the sound is everyone's.
                  *
                  * One control, three states. Idle it rings; while a strike
                  * is sounding it is held shut, so a second press cannot turn
                  * a marked silence into a clang; and once a sequence is
                  * running it becomes the way to call the rest of it off —
                  * a sequence you cannot stop is worse than no sequence, and
                  * a separate stop button would sit dead for whole sessions.
                  *
                  * The label carries each state, not only the fill, which is
                  * also how somebody who cannot hear the gong knows it went.
                  */}
                {gong.canRing && (
                    <div className="room-control-group">
                        <button
                            type="button"
                            onClick={running ? gong.stop : gong.ring}
                            disabled={!running && gong.ringing}
                            data-testid="gong"
                            className={`room-control ${shape} ${
                                gong.ringing || running ? "room-control-on" : ""
                            }`}
                        >
                            {running ? (
                                <BellOff size={18} aria-hidden="true" />
                            ) : (
                                <Bell
                                    size={18}
                                    aria-hidden="true"
                                    className={gong.ringing ? "animate-pulse" : ""}
                                />
                            )}
                            <span className={labelClass}>
                                {running
                                    ? t("meetingControls.gongStop")
                                    : gong.ringing
                                      ? t("meetingControls.gongRinging")
                                      : t("meetingControls.gong")}
                            </span>
                        </button>

                        <MeetingControlMenu
                            label={t("meetingControls.options.gong")}
                            testId="options-gong"
                            compact={compact}
                        >
                            {() => <MeetingGongSettings channel={gong} />}
                        </MeetingControlMenu>
                    </div>
                )}

                <MeetingLayoutPicker channel={layout} compact={compact} />

                {/*
                  * The control this whole pass is for.
                  *
                  * A patient on a handset gives a third of a small screen to
                  * the browser's address bar and to the room's header, and
                  * what is left is a face the size of a stamp. It is in the
                  * bar on every screen, because a therapist on a laptop wants
                  * the same thing during a silence.
                  */}
                <button
                    type="button"
                    onClick={fullscreen.toggle}
                    aria-pressed={fullscreen.active}
                    data-testid="fullscreen"
                    className={`room-control ${shape} ${
                        fullscreen.active ? "room-control-on" : ""
                    }`}
                >
                    {fullscreen.active ? (
                        <Minimize size={18} aria-hidden="true" />
                    ) : (
                        <Maximize size={18} aria-hidden="true" />
                    )}
                    <span className={labelClass}>
                        {fullscreen.active
                            ? t("meetingControls.fullscreenExit")
                            : t("meetingControls.fullscreen")}
                    </span>
                </button>
            </div>

            <div
                className={
                    compact ? "contents" : "flex items-center justify-end gap-2"
                }
            >
                {gong.transportFailed && (
                    <p
                        role="status"
                        className={`text-xs text-room-warn ${
                            compact ? "w-full text-center" : ""
                        }`}
                    >
                        {t("meetingControls.gongFailed")}
                    </p>
                )}
                {canLeave && (
                    <DisconnectButton
                        className={`room-control ${shape} room-control-end`}
                    >
                        <PhoneOff size={18} aria-hidden="true" />
                        <span className={labelClass}>
                            {t("meetingControls.leave")}
                        </span>
                    </DisconnectButton>
                )}
            </div>

        </div>
    );
}
